import { AgGridReact } from 'ag-grid-react';
import {
  IHeaderParams,
  ICellRendererParams,
  ColDef,
  ColumnResizedEvent,
  GridApi,
  GridReadyEvent,
  SortChangedEvent,
  IRowNode,
  SelectionChangedEvent
} from 'ag-grid-community';
import {
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { TableHeaderSelectionCheckbox } from 'common/table/components/TableHeaderSelectionCheckbox';
import { TableCellSelectionCheckbox } from 'common/table/components/TableCellSelectionCheckbox';
import { tableCustomTheme } from 'common/table/styles/tableCustomTheme';
import { EmptyState } from 'system/atoms/empty-states/EmptyState';
import {
  getColumnState,
  saveColumnState,
  clearColumnState
} from 'common/table/utils/tableV3Storage';
import { TableV3Sort, TableV3SelectionMode } from 'common/table/types/TableV3';
import { getSortedColumn } from 'common/table/utils/getSortedColumn';

export type TableV3AgGridRef = {
  resetTable: () => void;
  deselectAll: () => void;
  select: (ids: string[]) => void;
  setColumnsVisible: (keys: string[], visible: boolean) => void;
  refreshColumns: (keys: string[]) => void;
};

type TableV3AgGridProps<T> = {
  tableId: string;
  rowSelection: TableV3SelectionMode | undefined;
  columnDefs: ColDef<T>[];
  rowData: T[];
  context: any;
  loading: boolean;
  defaultSort: TableV3Sort<T> | 'unset';
  rowIdentifier?: keyof T;
  columnsCacheKey?: string; // Cache key - when this changes, columns are considered updated
  emptyStateComponent?: React.ReactNode;
  isFiltering?: boolean;
  // Opt-in: persist the user's column layout (order, width, pinning, sort) to
  // localStorage keyed by tableId. Visibility is not persisted: it comes from
  // the column definitions. Off by default so existing TableV3 consumers stay
  // unaffected.
  persistColumnState?: boolean;
  onSort?: (sort: TableV3Sort<T>) => void;
  onSelectionChanged?: (selectedIds: string[], selectedRows: T[]) => void;
  isRowSelectable?: (row: T) => boolean;
};

const sameIds = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((id) => seen.has(id));
};

const TableV3AgGridInner = <T,>(
  {
    tableId,
    rowSelection,
    columnDefs,
    rowData,
    context,
    loading,
    defaultSort,
    rowIdentifier,
    emptyStateComponent,
    isFiltering = false,
    persistColumnState = false,
    onSort,
    onSelectionChanged,
    isRowSelectable
  }: TableV3AgGridProps<T>,
  ref: React.Ref<TableV3AgGridRef>
) => {
  const selectionRef = useRef<string[]>([]);
  const selectedRowsRef = useRef(new Map<string, T>());
  const gridApiRef = useRef<GridApi | null>(null);
  // Suppresses onSaveColumnState while resetTable is mid-flight. AG Grid fires
  // onColumnVisible / onColumnMoved synchronously during resetColumnState and
  // applyColumnState; without this flag those events would re-persist the
  // intermediate state to localStorage immediately after we cleared the key.
  const isResettingRef = useRef(false);

  const currentColIds = useMemo(
    () =>
      columnDefs
        .map((def) => def.colId ?? (def.field as string | undefined))
        .filter((id): id is string => typeof id === 'string'),
    [columnDefs]
  );

  const rowIdOf = (node: IRowNode<T>) => {
    const id = rowIdentifier ? node.data?.[rowIdentifier] : undefined;
    return id === undefined ? undefined : String(id);
  };

  const gridSelectedIds = (api: GridApi) =>
    api
      .getSelectedNodes()
      .map(rowIdOf)
      .filter((id): id is string => id !== undefined);

  // Rows leave the grid when the page turns, so their data is kept here:
  // consumers that rebuild from the rows alone would otherwise drop every
  // selection made on a page they are no longer looking at.
  const rememberRows = (api: GridApi | null) => {
    api?.getSelectedNodes().forEach((node) => {
      const id = rowIdOf(node);
      if (id !== undefined && node.data) {
        selectedRowsRef.current.set(id, node.data);
      }
    });
  };

  const commitSelection = (next: string[], api: GridApi | null) => {
    if (sameIds(selectionRef.current, next)) return;
    selectionRef.current = next;

    rememberRows(api);
    const keep = new Set(next);
    selectedRowsRef.current.forEach((_row, id) => {
      if (!keep.has(id)) selectedRowsRef.current.delete(id);
    });

    onSelectionChanged?.(
      next,
      next
        .map((id) => selectedRowsRef.current.get(id))
        .filter((row): row is T => row !== undefined)
    );
  };

  const applySelectionToGrid = (api: GridApi) => {
    if (!rowIdentifier) return;
    const wanted = new Set(selectionRef.current);
    const selectedNow = new Set(gridSelectedIds(api));
    const toSelect: IRowNode<T>[] = [];
    const toDeselect: IRowNode<T>[] = [];

    api.forEachNode((node) => {
      const id = rowIdOf(node);
      if (id === undefined) return;
      if (wanted.has(id) && !selectedNow.has(id)) toSelect.push(node);
      else if (!wanted.has(id) && selectedNow.has(id)) toDeselect.push(node);
    });

    if (toDeselect.length > 0) {
      api.setNodesSelected({ nodes: toDeselect, newValue: false });
    }
    if (toSelect.length > 0) {
      api.setNodesSelected({ nodes: toSelect, newValue: true });
    }

    rememberRows(api);
  };

  /**
   * Puts the grid into its starting column state: the user's persisted layout if
   * there is one, otherwise the table's default sort, then repaints.
   *
   * Called from both onGridReady and the data effect below because either can be
   * the one that runs last. On a cold load the data arrives after the grid is
   * ready, so the effect does it; on a warm load (Apollo cache hit, rowData
   * present on the first render) the effect runs and bails before the api exists,
   * and only onGridReady is left to do it. Applying from just one of the two left
   * the default sort unapplied on whichever path lost the race — which is what
   * made the sorted-column highlight flaky on load rather than absent.
   *
   * The repaint is forced: `ag-grid-sorted-column` is a cellClassRule keyed on
   * column state, not on cell values, so an unforced refresh skips every cell
   * whose value has not changed and leaves the class stale.
   */
  const applyInitialColumnState = (api: GridApi) => {
    const savedState = persistColumnState
      ? getColumnState(tableId, currentColIds)
      : [];

    if (savedState.length > 0) {
      api.applyColumnState({ state: savedState, applyOrder: true });
    }

    // Sort settled after the saved layout, not instead of it: `defaultSort` is a
    // live prop, persisted state is a stale cache. Only one column sorts.
    if (defaultSort !== 'unset') {
      api.applyColumnState({
        state: api.getColumnState().map((col) => ({
          ...col,
          sort: col.colId === defaultSort.field ? defaultSort.direction : null
        })),
        applyOrder: true
      });
    } else if (savedState.length > 0) {
      api.applyColumnState({
        state: api.getColumnState().map((col) => ({ ...col, sort: null })),
        applyOrder: true
      });
    }

    api.refreshCells({ force: true });
    api.refreshHeader();
  };

  const onGridReady = (event: GridReadyEvent) => {
    gridApiRef.current = event.api;

    applyInitialColumnState(event.api);
    applySelectionToGrid(event.api);
  };

  const resetTable = () => {
    isResettingRef.current = true;
    try {
      // Reset column state to default (ordering, widths, etc.)
      if (gridApiRef.current) {
        // Save current visibility state before reset
        const currentState = gridApiRef.current.getColumnState();
        const visibilityState = currentState.map((col) => ({
          colId: col.colId,
          hide: col.hide
        }));

        gridApiRef.current.resetColumnState();

        // Restore visibility state after reset
        const newState = gridApiRef.current.getColumnState();
        const stateWithVisibility = newState.map((col) => {
          const savedVisibility = visibilityState.find(
            (v) => v.colId === col.colId
          );
          return savedVisibility ? { ...col, hide: savedVisibility.hide } : col;
        });

        gridApiRef.current.applyColumnState({
          state: stateWithVisibility,
          applyOrder: true
        });

        gridApiRef.current.refreshHeader();
        // Forced for the same reason as in onSortChanged: the sorted-column
        // class rule reads column state, not cell values, so an unforced refresh
        // leaves it stale.
        gridApiRef.current.refreshCells({ force: true });

        // Apply default sort if it exists
        if (defaultSort !== 'unset') {
          const currentStateForSort = gridApiRef.current.getColumnState();
          const stateWithDefaultSort = currentStateForSort.map((col) => {
            if (col.colId === defaultSort.field) {
              return { ...col, sort: defaultSort.direction };
            }
            return col;
          });

          gridApiRef.current.applyColumnState({
            state: stateWithDefaultSort,
            applyOrder: true
          });

          onSort?.(defaultSort);
        }
      }
    } finally {
      // Wipe the persisted state AFTER the grid operations so the synchronous
      // column events fired above don't repopulate localStorage via the save
      // handlers (which are no-ops while isResettingRef is true).
      isResettingRef.current = false;
      if (persistColumnState) {
        clearColumnState(tableId);
      }
    }
  };

  const deselectAll = () => {
    gridApiRef.current?.deselectAll();
    commitSelection([], gridApiRef.current);
  };

  const select = (ids: string[]) => {
    selectionRef.current = [...new Set(ids)];
    if (gridApiRef.current) applySelectionToGrid(gridApiRef.current);
  };

  const setColumnsVisible = (keys: string[], visible: boolean) => {
    if (!gridApiRef.current) {
      return;
    }

    gridApiRef.current.setColumnsVisible(keys, visible);
  };

  // Repaints cells that read data from outside the row, e.g. React context.
  const refreshColumns = (keys: string[]) => {
    if (!gridApiRef.current) {
      return;
    }

    gridApiRef.current.refreshCells({ columns: keys, force: true });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    resetTable,
    deselectAll,
    select,
    setColumnsVisible,
    refreshColumns
  }));

  const onSaveColumnState = () => {
    if (!persistColumnState || loading || isResettingRef.current) return;
    saveColumnState(tableId, gridApiRef.current?.getColumnState() || []);
  };

  // AG Grid fires onColumnResized for every intermediate pixel during a
  // drag; only the event with finished=true represents the user releasing
  // the mouse. Save only on completion to avoid hammering localStorage.
  const onColumnResizedFinished = (event: ColumnResizedEvent) => {
    if (event.finished) onSaveColumnState();
  };

  const onSortChanged = (event: SortChangedEvent) => {
    const columnState = event.api.getColumnState();
    const sortedColumn = getSortedColumn<T>(columnState);

    // Always persist on sort change so that clearing a sort (asc → desc → none)
    // overwrites the previously-stored sort state instead of leaving it stale.
    onSaveColumnState();

    if (sortedColumn) {
      onSort?.(sortedColumn);

      // Force the repaint. `ag-grid-sorted-column` is a cellClassRule keyed on
      // the grid's column state rather than on any cell's value, and an unforced
      // refresh skips every cell whose value is unchanged — which is all of them
      // when the sort is applied to data that already arrived in that order. The
      // highlight then never appears until something else happens to redraw the
      // cell.
      event.api.refreshCells({ force: true });
      event.api.refreshHeader();
    }
  };

  // The grid is the truth only for rows it holds; everything else is carried
  // over, so paging, filtering and our own restores recompute the same set.
  const onSelectionChangedGrid = (event: SelectionChangedEvent) => {
    if (!rowIdentifier) return;
    const held = new Set<string>();
    event.api.forEachNode((node) => {
      const id = rowIdOf(node);
      if (id !== undefined) held.add(id);
    });

    const visible = gridSelectedIds(event.api);
    const carried =
      rowSelection === 'singleRow' && visible.length > 0
        ? []
        : selectionRef.current.filter((id) => !held.has(id));

    commitSelection([...carried, ...visible], event.api);
  };

  // Header components read filter state off the grid context. AG Grid doesn't
  // re-render them when context changes, so force it or badges go stale after
  // a new search.
  useEffect(() => {
    gridApiRef.current?.refreshHeader();
  }, [context]);

  // Manually control loading overlay
  useEffect(() => {
    if (!gridApiRef.current) return;

    if (loading) {
      gridApiRef.current.showLoadingOverlay();
    } else if (rowData.length === 0) {
      gridApiRef.current.showNoRowsOverlay();
    } else {
      gridApiRef.current.hideOverlay();
    }
  }, [loading, rowData]);

  useEffect(() => {
    if (!gridApiRef.current || loading) return;
    applyInitialColumnState(gridApiRef.current);
  }, [
    loading,
    rowData,
    currentColIds,
    tableId,
    defaultSort,
    persistColumnState
  ]);

  useEffect(() => {
    if (gridApiRef.current) applySelectionToGrid(gridApiRef.current);
  }, [rowData]);

  return useMemo(
    () => (
      <AgGridReact
        defaultColDef={{
          sortable: false,
          sortingOrder: ['asc', 'desc'],
          wrapText: true,
          autoHeight: true
        }}
        selectionColumnDef={{
          pinned: 'left',
          lockPosition: true,
          width: 60,
          maxWidth: 60,
          minWidth: 60,
          headerComponent: (headerColumn: IHeaderParams) => (
            <TableHeaderSelectionCheckbox
              params={headerColumn}
              selectionMode={rowSelection}
              onDeselectAll={deselectAll}
            />
          ),
          cellRenderer: (params: ICellRendererParams<T>) => (
            <TableCellSelectionCheckbox
              params={params}
              selectionMode={rowSelection}
            />
          )
        }}
        rowSelection={
          rowSelection
            ? {
                mode: rowSelection,
                isRowSelectable: isRowSelectable
                  ? (node) => (node.data ? isRowSelectable(node.data) : true)
                  : undefined
              }
            : undefined
        }
        columnDefs={columnDefs}
        rowData={rowData}
        context={context}
        theme={tableCustomTheme}
        domLayout="autoHeight"
        enableCellTextSelection
        ensureDomOrder
        suppressCellFocus
        suppressRowHoverHighlight
        suppressAnimationFrame
        suppressDragLeaveHidesColumns
        animateRows={false}
        suppressColumnMoveAnimation
        loading={loading}
        loadingOverlayComponent={() => <EmptyState type="loading" />}
        noRowsOverlayComponent={() => (
          <>
            {emptyStateComponent ? (
              emptyStateComponent
            ) : (
              <EmptyState type={isFiltering ? 'no-results' : 'no-records'} />
            )}
          </>
        )}
        onGridReady={onGridReady}
        onColumnResized={onColumnResizedFinished}
        onColumnMoved={onSaveColumnState}
        onColumnVisible={onSaveColumnState}
        onColumnPinned={onSaveColumnState}
        onSortChanged={onSortChanged}
        onSelectionChanged={onSelectionChangedGrid}
      />
    ),
    [loading, rowData]
  );
};

export const TableV3AgGrid = forwardRef(TableV3AgGridInner) as any;
